import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import apiFetch from "../../../../shared/services/apiClient";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import { usePDFIndividual } from "./EstadoFinanciero";

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) =>
  Math.abs(Number(n ?? 0)).toLocaleString("es-CR", { minimumFractionDigits: 2 });

const NIVEL_INDENT = { 1: 0, 2: 16, 3: 28, 4: 40 };
const NIVEL_COLOR  = { 1: "#f97316", 2: "#e5ecf5", 3: "#d1d5db", 4: "#9ca3af" };
const NIVEL_WEIGHT = { 1: 700, 2: 700, 3: 600, 4: 400 };
const NIVEL_BG     = { 1: "rgba(249,115,22,0.06)", 2: "rgba(55,65,81,0.25)", 3: "transparent", 4: "transparent" };

// ── Fila ──────────────────────────────────────────────────────────────────────
function Fila({ fila, collapsed, onToggle }) {
  const indent = NIVEL_INDENT[fila.nivel] ?? 0;
  const ba     = Number(fila.balance_anterior ?? 0);
  const deb    = Number(fila.debitos  ?? 0);
  const cred   = Number(fila.creditos ?? 0);
  const final  = Number(fila.saldo_final ?? 0);

  return (
    <tr style={{ background: NIVEL_BG[fila.nivel], borderBottom: "1px solid #1a2233" }}>
      <td style={{ fontFamily:"monospace", fontSize:"0.75rem", color:"#6b7280",
        whiteSpace:"nowrap", padding:"6px 10px" }}>
        {fila.codigo}
      </td>
      <td style={{ padding:"6px 8px", paddingLeft: indent + 8 }}>
        <div style={{ display:"flex", alignItems:"center", gap:5 }}>
          {fila.tiene_hijos ? (
            <button onClick={() => onToggle(fila.id)}
              style={{ background:"none", border:"none", cursor:"pointer",
                color:"#6b7280", padding:0, fontSize:"0.65rem", width:14, flexShrink:0 }}>
              <i className={`fa-solid fa-chevron-${collapsed.has(fila.id) ? "right" : "down"}`}/>
            </button>
          ) : <span style={{ width:14, flexShrink:0 }}/>}
          <span style={{ fontWeight: NIVEL_WEIGHT[fila.nivel], color: NIVEL_COLOR[fila.nivel],
            fontSize: fila.nivel === 1 ? "0.88rem" : "0.83rem" }}>
            {fila.nombre}
          </span>
        </div>
      </td>
      {[
        { val: ba,    zero: "—" },
        { val: deb,   zero: "—", color: "#60a5fa" },
        { val: cred,  zero: "—", color: "#c084fc" },
        { val: final, zero: "₡0,00", color: final < -0.01 ? "#f87171" : final > 0.01 ? "#4ade80" : "#374151" },
      ].map((col, i) => (
        <td key={i} className="text-end" style={{ fontFamily:"monospace",
          fontSize:"0.8rem", padding:"6px 12px",
          color: Math.abs(col.val) < 0.01 ? "#374151" : (col.color || "#9ca3af"),
          fontWeight: (i === 3 && fila.nivel <= 2) ? 700 : 400 }}>
          {Math.abs(col.val) < 0.01 ? col.zero
            : `${col.val < 0 ? "-" : ""}₡${fmt(Math.abs(col.val))}`}
        </td>
      ))}
    </tr>
  );
}

// ── Página ────────────────────────────────────────────────────────────────────
export default function BalanceComprobacion() {
  const now       = new Date();
  const mesInicio = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
  const mesFin    = new Date(now.getFullYear(), now.getMonth()+1, 0).toISOString().split("T")[0];

  const [fechaDesde, setFechaDesde] = useState(mesInicio);
  const [fechaHasta, setFechaHasta] = useState(mesFin);
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [soloConMov, setSoloConMov] = useState(true);
  const [collapsed,  setCollapsed]  = useState(new Set());
  const pdfBC = usePDFIndividual(
    '/api/finance/reportes/balance-comprobacion-pdf/',
    'balance_comprobacion.pdf'
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (fechaDesde) qs.set("fecha_desde", fechaDesde);
      if (fechaHasta) qs.set("fecha_hasta", fechaHasta);
      const res  = await apiFetch(`/api/finance/reportes/balance-comprobacion/?${qs}`);
      setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [fechaDesde, fechaHasta]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filas      = data?.filas ?? [];
  const childrenOf = buildChildrenOf(filas);
  const hidden     = buildHidden(filas, childrenOf, collapsed);

  const tieneMovArbol = (id) => {
    const f = filas.find(x => x.id === id);
    if (!f) return false;
    if (f.tiene_movimientos) return true;
    return (childrenOf[id] ?? []).some(tieneMovArbol);
  };

  const visibles = filas.filter(f =>
    !hidden.has(f.id) && (!soloConMov || tieneMovArbol(f.id))
  );

  const totalBA   = filas.filter(f => f.nivel === 1).reduce((a,f) => a + Number(f.balance_anterior ?? 0), 0);
  const totalDeb  = Number(data?.total_debitos  ?? 0);
  const totalCred = Number(data?.total_creditos ?? 0);
  const bal       = data?.balanceado ?? true;

  return (
    <>
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>Inicio /</span>
        </Link>
          <span className={invoiceStyles.active}> Balance de Comprobación</span>
      </span>
      <div>
        <h2 className="title mt-2 mb-2">Balance de Comprobación</h2>
        <p className="mb-3" style={{color:"#9ca3af"}}>Movimientos del catálogo de cuentas por período</p>
      </div>

      <FiltrosBarra
        fechaDesde={fechaDesde} setFechaDesde={setFechaDesde}
        fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
        soloConMov={soloConMov} setSoloConMov={setSoloConMov}
        onActualizar={fetchData} loading={loading}
        extraButtons={
          <>
            <button onClick={() => pdfBC.generar(`fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`)}
              disabled={pdfBC.loading || loading || !data}
              style={{ padding:'7px 14px', background:'rgba(59,130,246,0.15)',
                border:'1px solid rgba(59,130,246,0.5)', borderRadius:6,
                color:'#60a5fa', fontWeight:600, cursor:'pointer', fontSize:'0.85rem',
                opacity:(pdfBC.loading||!data)?0.5:1 }}>
              {pdfBC.loading
                ? <><i className='fa-solid fa-circle-notch fa-spin'/> Generando…</>
                : <><i className='fa-solid fa-file-pdf' style={{marginRight:5}}/>PDF</>}
            </button>
            {pdfBC.modal}
          </>
        }
      />

      {data && (
        <CardsResumen cards={[
          { label:"Balance Anterior", value:totalBA,   color:"#9ca3af", icon:"fa-clock-rotate-left" },
          { label:"Total Débitos",    value:totalDeb,  color:"#60a5fa", icon:"fa-arrow-up" },
          { label:"Total Créditos",   value:totalCred, color:"#c084fc", icon:"fa-arrow-down" },
          { label: bal ? "Balanceado ✓" : "Diferencia ⚠",
            value: Math.abs(Number(data.diferencia)),
            color: bal ? "#4ade80" : "#f87171",
            icon:  bal ? "fa-scale-balanced" : "fa-triangle-exclamation" },
        ]}/>
      )}

      <div className={invoiceStyles["table-container"]}>
        <TableHeader
          count={visibles.length} loading={loading}
          onColapsar={() => setCollapsed(new Set(filas.filter(f => f.nivel < 4).map(f => f.id)))}
          onExpandir={() => setCollapsed(new Set())}
        />
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th style={{ width:130 }}>Código</th>
                <th>Nombre</th>
                <th className="text-end" style={{ width:140 }}>Bal. Anterior</th>
                <th className="text-end" style={{ width:130 }}>Débitos</th>
                <th className="text-end" style={{ width:130 }}>Créditos</th>
                <th className="text-end" style={{ width:130 }}>Saldo Final</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <FilaCargando colSpan={6} msg="Calculando balance de comprobación…"/>
               : visibles.length === 0 ? <FilaVacia colSpan={6} soloConMov={soloConMov} onMostrarTodas={() => setSoloConMov(false)}/>
               : visibles.map(f => <Fila key={f.id} fila={f} collapsed={collapsed} onToggle={id => setCollapsed(prev => toggle(prev, id))}/>)}
            </tbody>
            {!loading && data && (
              <tfoot>
                <tr style={{ background:"rgba(249,115,22,0.08)", borderTop:"2px solid rgba(249,115,22,0.4)" }}>
                  <td colSpan={2} style={{ fontWeight:700, color:"#f97316", padding:"10px 12px", fontSize:"0.87rem" }}>TOTALES</td>
                  <td className="text-end" style={{ fontFamily:"monospace", fontWeight:700, color:"#9ca3af", padding:"10px 12px" }}>₡{fmt(totalBA)}</td>
                  <td className="text-end" style={{ fontFamily:"monospace", fontWeight:700, color:"#60a5fa", padding:"10px 12px" }}>₡{fmt(totalDeb)}</td>
                  <td className="text-end" style={{ fontFamily:"monospace", fontWeight:700, color:"#c084fc", padding:"10px 12px" }}>₡{fmt(totalCred)}</td>
                  <td className="text-end" style={{ fontFamily:"monospace", fontWeight:700, color: bal ? "#4ade80" : "#f87171", padding:"10px 12px" }}>
                    {bal ? <><i className="fa-solid fa-check" style={{ marginRight:4 }}/>Balanceado</> : `₡${fmt(data.diferencia)}`}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </>
  );
}

// ── Shared helpers (exported for reuse) ──────────────────────────────────────
export function buildChildrenOf(filas) {
  const m = {};
  for (const f of filas) {
    if (f.padre_id != null) { if (!m[f.padre_id]) m[f.padre_id] = []; m[f.padre_id].push(f.id); }
  }
  return m;
}
export function buildHidden(filas, childrenOf, collapsed) {
  const h = new Set();
  for (const f of filas) {
    if (collapsed.has(f.id)) {
      const q = [...(childrenOf[f.id] ?? [])];
      while (q.length) { const id = q.shift(); h.add(id); q.push(...(childrenOf[id] ?? [])); }
    }
  }
  return h;
}
export function toggle(prev, id) {
  const next = new Set(prev);
  next.has(id) ? next.delete(id) : next.add(id);
  return next;
}

// ── Shared UI components ─────────────────────────────────────────────────────
export function FiltrosBarra({ fechaDesde, setFechaDesde, fechaHasta, setFechaHasta,
    soloConMov, setSoloConMov, onActualizar, loading, extraButtons }) {
  return (
    <div style={{ background:"#1f2937", border:"1px solid #374151", borderRadius:8,
      padding:"14px 18px", marginBottom:16,
      display:"flex", flexWrap:"wrap", gap:14, alignItems:"flex-end" }}>
      {fechaDesde !== undefined && (
        <div>
          <label style={{ fontSize:"0.78rem", color:"#9ca3af", display:"block", marginBottom:4 }}>Desde</label>
          <input type="date" value={fechaDesde} onChange={e => {
            const desde = e.target.value;
            setFechaDesde(desde);
            // Auto-calcular fin de mes
            if (desde && setFechaHasta) {
              const [y, m] = desde.split('-').map(Number);
              const finMes = new Date(y, m, 0); // día 0 del mes siguiente = último día del mes actual
              const dd = String(finMes.getDate()).padStart(2, '0');
              const mm = String(m).padStart(2, '0');
              setFechaHasta(`${y}-${mm}-${dd}`);
            }
          }}
            style={{ background:"#344152", border:"1px solid #364250", borderRadius:6,
              color:"#e5ecf5", padding:"6px 10px", fontSize:"0.85rem", colorScheme:"dark" }}/>
        </div>
      )}
      {fechaHasta !== undefined && (
        <div>
          <label style={{ fontSize:"0.78rem", color:"#9ca3af", display:"block", marginBottom:4 }}>
            {fechaDesde !== undefined ? "Hasta" : "Fecha de corte"}
          </label>
          <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)}
            style={{ background:"#344152", border:"1px solid #364250", borderRadius:6,
              color:"#e5ecf5", padding:"6px 10px", fontSize:"0.85rem", colorScheme:"dark" }}/>
        </div>
      )}
      {soloConMov !== undefined && (
        <label style={{ display:"flex", alignItems:"center", gap:6,
          fontSize:"0.83rem", color:"#9ca3af", cursor:"pointer" }}>
          <input type="checkbox" checked={soloConMov} onChange={e => setSoloConMov(e.target.checked)}
            style={{ accentColor:"#f97316" }}/>
          Ocultar cuentas sin movimiento
        </label>
      )}
      <button onClick={onActualizar} disabled={loading}
        style={{ padding:"7px 16px", background:"#f97316", border:"none",
          borderRadius:6, color:"#fff", fontWeight:600, cursor:"pointer",
          fontSize:"0.85rem", opacity:loading?0.7:1 }}>
        {loading
          ? <><i className="fa-solid fa-circle-notch fa-spin"/> Calculando…</>
          : <><i className="fa-solid fa-rotate-right"/> Actualizar</>}
      </button>
      {extraButtons}
    </div>
  );
}

export function CardsResumen({ cards }) {
  return (
    <div style={{ display:"grid", gridTemplateColumns:`repeat(${cards.length},1fr)`, gap:12, marginBottom:16 }}>
      {cards.map(card => (
        <div key={card.label} style={{ background:"#1f2937", border:`1px solid ${card.color}30`,
          borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ width:38, height:38, borderRadius:9, flexShrink:0,
            background:`${card.color}15`, border:`1px solid ${card.color}30`,
            display:"flex", alignItems:"center", justifyContent:"center" }}>
            <i className={`fa-solid ${card.icon}`} style={{ color:card.color, fontSize:"0.95rem" }}/>
          </div>
          <div>
            <p style={{ margin:0, color:"#6b7280", fontSize:"0.68rem", fontWeight:600,
              textTransform:"uppercase", letterSpacing:"0.4px" }}>{card.label}</p>
            <p style={{ margin:"2px 0 0", color:"#f9fafb", fontSize:"0.92rem",
              fontWeight:700, fontFamily:"monospace", whiteSpace:"nowrap" }}>
              {card.prefix || "₡"}{fmt(card.value)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function TableHeader({ count, loading, onColapsar, onExpandir }) {
  return (
    <div className={`${invoiceStyles["table-header"]} d-flex justify-content-between align-items-center`}>
      <span style={{ fontSize:"0.81rem", color:"#9ca3af" }}>
        {loading ? "Calculando…" : `${count} cuenta(s)`}
      </span>
      <div style={{ display:"flex", gap:6 }}>
        <button onClick={onColapsar} style={{ padding:"3px 10px", background:"transparent",
          border:"1px solid #374151", borderRadius:5, color:"#9ca3af", fontSize:"0.74rem", cursor:"pointer" }}>
          Colapsar todo
        </button>
        <button onClick={onExpandir} style={{ padding:"3px 10px", background:"transparent",
          border:"1px solid #374151", borderRadius:5, color:"#9ca3af", fontSize:"0.74rem", cursor:"pointer" }}>
          Expandir todo
        </button>
      </div>
    </div>
  );
}

export function FilaCargando({ colSpan, msg }) {
  return (
    <tr><td colSpan={colSpan} style={{ textAlign:"center", padding:48, color:"#9ca3af" }}>
      <i className="fa-solid fa-circle-notch fa-spin"
         style={{ color:"#f97316", fontSize:"1.4rem", display:"block", marginBottom:10 }}/>
      {msg || "Calculando…"}
    </td></tr>
  );
}

export function FilaVacia({ colSpan, soloConMov, onMostrarTodas }) {
  return (
    <tr><td colSpan={colSpan} style={{ textAlign:"center", padding:40, color:"#6b7280" }}>
      No hay datos para el período seleccionado.
      {soloConMov && onMostrarTodas && (
        <button onClick={onMostrarTodas} style={{ marginLeft:8, background:"none", border:"none",
          color:"#f97316", cursor:"pointer", fontSize:"0.83rem", textDecoration:"underline" }}>
          Mostrar todas las cuentas
        </button>
      )}
    </td></tr>
  );
}

export function FilaArbol({ fila, collapsed, onToggle,
    colorFn, valorFn, bgFn, colSpan = 1 }) {
  const indent = NIVEL_INDENT[fila.nivel] ?? 0;
  const rowBg  = bgFn ? bgFn(fila) : NIVEL_BG[fila.nivel];
  return (
    <tr style={{ background: rowBg, borderBottom:"1px solid #1a2233" }}>
      <td style={{ fontFamily:"monospace", fontSize:"0.75rem", color:"#6b7280",
        whiteSpace:"nowrap", padding:"6px 10px" }}>
        {fila.codigo}
      </td>
      <td style={{ padding:"6px 8px", paddingLeft: indent + 8 }}>
        <div style={{ display:"flex", alignItems:"center", gap:5 }}>
          {fila.tiene_hijos ? (
            <button onClick={() => onToggle(fila.id)}
              style={{ background:"none", border:"none", cursor:"pointer",
                color:"#6b7280", padding:0, fontSize:"0.65rem", width:14, flexShrink:0 }}>
              <i className={`fa-solid fa-chevron-${collapsed.has(fila.id) ? "right" : "down"}`}/>
            </button>
          ) : <span style={{ width:14, flexShrink:0 }}/>}
          <span style={{ fontWeight: NIVEL_WEIGHT[fila.nivel],
            color: colorFn ? colorFn(fila) : NIVEL_COLOR[fila.nivel],
            fontSize: fila.nivel === 1 ? "0.88rem" : "0.83rem" }}>
            {fila.nombre}
          </span>
        </div>
      </td>
      <td className="text-end" style={{ fontFamily:"monospace", fontSize:"0.83rem",
        padding:"6px 14px", fontWeight: fila.nivel <= 2 ? 700 : 400,
        color: valorFn ? valorFn(fila).color : NIVEL_COLOR[fila.nivel] }}>
        {valorFn ? valorFn(fila).text : ""}
      </td>
    </tr>
  );
}
